// src/config/api.ts

import axios from 'axios';
import type { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { ApiError, type ApiErrorResponse } from '../shared/types/errors';
import { config } from './env';
import { getActiveBrandProductName } from '../shared/utils/brandName';

const API_URL = config.API_URL;
const API_PREFIX = config.API_PREFIX;

const apiClient = axios.create({
  baseURL: `${API_URL}${API_PREFIX}`,
  timeout: 130000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Flag to prevent multiple refresh attempts
let isRefreshing = false;
let refreshSubscribers: Array<{ resolve: (token: string) => void; reject: (err: Error) => void }> = [];

// Subscribe to token refresh
function subscribeTokenRefresh(cb: { resolve: (token: string) => void; reject: (err: Error) => void }) {
  refreshSubscribers.push(cb);
}

// Notify all subscribers when token is refreshed
function onRefreshed(token: string) {
  refreshSubscribers.forEach((cb) => cb.resolve(token));
  refreshSubscribers = [];
}

// Notify all subscribers when refresh fails
function onRefreshFailed(err: Error) {
  refreshSubscribers.forEach((cb) => cb.reject(err));
  refreshSubscribers = [];
}

// Interceptor para agregar token de autenticación
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // White-label: marca activa para que el backend resuelva la búsqueda de
  // plantillas de forma agnóstica a la marca (en BD siguen siendo "Ainovis"
  // aunque la UI muestre "ALMA").
  config.headers['X-Brand-Name'] = getActiveBrandProductName();
  return config;
});

// Interceptor para manejar errores y refresh automático
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiErrorResponse>) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Si es 401 y no es la ruta de refresh ni login
    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/auth/login') &&
      !originalRequest.url?.includes('/auth/refresh')
    ) {
      const token = localStorage.getItem('authToken');

      if (!token) {
        // No hay token, redirigir al login
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        window.location.href = '/login';
        // Same reasoning as the refresh-failure branch below: do not let the
        // rejection escape to Sentry while we're already redirecting.
        return new Promise(() => undefined);
      }

      if (isRefreshing) {
        // Ya hay un refresh en progreso, esperar a que termine
        return new Promise((resolve, reject) => {
          subscribeTokenRefresh({
            resolve: (newToken: string) => {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              resolve(apiClient(originalRequest));
            },
            reject: () => {
              // Refresh failed — redirect to login (same as the catch below)
              window.location.href = '/login';
              resolve(new Promise(() => undefined));
            },
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Intentar refresh con el token expirado (grace period de 30 min en backend)
        const response = await axios.post(
          `${API_URL}${API_PREFIX}/auth/refresh`,
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const { token: newToken, user } = response.data;

        // Guardar nuevo token y usuario
        localStorage.setItem('authToken', newToken);
        localStorage.setItem('user', JSON.stringify(user));

        // Actualizar header de la petición original
        originalRequest.headers.Authorization = `Bearer ${newToken}`;

        // Notificar a todas las peticiones en espera
        onRefreshed(newToken);
        isRefreshing = false;

        // Reintentar la petición original
        return apiClient(originalRequest);
      } catch (refreshError) {
        // Refresh falló (token expiró hace más de 5 min), hacer logout
        isRefreshing = false;
        onRefreshFailed(new SessionExpiredError());
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        window.location.href = '/login';
        // We are redirecting away; rejecting here would surface as an
        // unhandled rejection (Sentry issue 118499114) for any caller that
        // didn't wrap the request in try/catch — a fire-and-forget call is
        // common for prefetch/cache hydration. Hold the promise unresolved
        // until the navigation tears the page down.
        void refreshError;
        return new Promise(() => undefined);
      }
    }

    // Normalizar el error antes de rechazar
    return Promise.reject(normalizeError(error));
  }
);

/**
 * Normaliza errores de axios a ApiError para manejo consistente.
 * Detecta errores anidados en error_details.data.error para mostrar el error más específico.
 */
function normalizeError(error: AxiosError<ApiErrorResponse>): ApiError {
  // Error del backend con estructura conocida: { success: false, error: { code, message, ... } }
  if (error.response?.data?.error && typeof error.response.data.error === 'object') {
    const errorData = error.response.data.error;
    
    // 🔍 Revisar si hay un error más específico anidado en error_details.data.error
    // Esto ocurre cuando un servicio envuelve errores de otros servicios
    if (errorData.details?.data?.error) {
      const nestedError = errorData.details.data.error;
      
      // Si el error anidado tiene código, usarlo (es más específico)
      if (nestedError.code) {
        return new ApiError({
          code: nestedError.code,
          message: nestedError.message || errorData.message,
          status: errorData.status,
          path: errorData.path,
          timestamp: errorData.timestamp,
          details: nestedError.details || errorData.details,
        });
      }
    }
    
    // Si no hay error anidado, usar el error original
    return new ApiError(errorData);
  }

  // Error HTTP sin estructura de backend (puede incluir { success: false, message: '...' })
  if (error.response) {
    const responseMessage = (error.response.data as any)?.message;
    return new ApiError({
      code: 'NETWORK_ERROR',
      message: responseMessage || error.message || 'Error de red',
      status: error.response.status,
      path: error.config?.url || '',
      timestamp: new Date().toISOString(),
    });
  }

  // Request hecho pero sin respuesta
  if (error.request) {
    return new ApiError({
      code: 'NO_RESPONSE',
      message: 'No se recibió respuesta del servidor',
      status: 0,
      path: error.config?.url || '',
      timestamp: new Date().toISOString(),
    });
  }

  // Error configurando request
  return new ApiError({
    code: 'REQUEST_SETUP_ERROR',
    message: error.message || 'Error al configurar la solicitud',
    status: 0,
    path: '',
    timestamp: new Date().toISOString(),
  });
}

/**
 * Error thrown when the refresh grace period has been exceeded.
 * Callers can catch this to persist data before the session is torn down.
 */
export class SessionExpiredError extends Error {
  constructor() {
    super('Session expired beyond grace period');
    this.name = 'SessionExpiredError';
  }
}

/**
 * Refresh the auth token using the expired token (30-min grace period on backend).
 * Reuses the same deduplication logic as the axios interceptor so concurrent
 * callers (fetch-based SSE streams, axios requests) don't race.
 * Returns the fresh token string.
 * Throws SessionExpiredError when the grace period is exceeded so callers can
 * persist work before being redirected to login.
 */
export async function refreshAuthToken(): Promise<string> {
  const token = localStorage.getItem('authToken');

  if (!token) {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    throw new SessionExpiredError();
  }

  if (isRefreshing) {
    // Another refresh is already in flight – wait for it
    return new Promise<string>((resolve, reject) => {
      subscribeTokenRefresh({ resolve, reject });
    });
  }

  isRefreshing = true;

  try {
    const response = await axios.post(
      `${API_URL}${API_PREFIX}/auth/refresh`,
      {},
      // Cap the wait so a stalled network can't leave a proactive/silent refresh
      // hanging forever (the session watchdog would stay disarmed). On timeout
      // this rejects → callers fall back (expiry modal / logout).
      { headers: { Authorization: `Bearer ${token}` }, timeout: 15_000 },
    );

    const { token: newToken, user } = response.data;
    localStorage.setItem('authToken', newToken);
    localStorage.setItem('user', JSON.stringify(user));
    onRefreshed(newToken);
    isRefreshing = false;
    return newToken;
  } catch (refreshError) {
    isRefreshing = false;
    const expired = new SessionExpiredError();
    onRefreshFailed(expired);
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    throw expired;
  }
}

export default apiClient;